import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../nav_bar/Navbar";
import "./MyBookingsPage.css";
import { fetchBookingsByEmail } from "./clientEventService";

const API_BASE = "/api/public/events";

/**
 * ============================
 * SAFE DEBUG LOGGER
 * ============================
 * Will NOT log in production
 */
const isDev = process.env.NODE_ENV === "development";

const debugLog = (...args) => {
  if (isDev) {
    console.log("[MyBookingsPage]", ...args);
  }
};

const debugError = (...args) => {
  if (isDev) {
    console.error("[MyBookingsPage]", ...args);
  }
};

const MyBookingsPage = () => {

const navigate = useNavigate();

const [email, setEmail] = useState("");
const [inputEmail, setInputEmail] = useState("");
const [bookings, setBookings] = useState([]);
const [loading, setLoading] = useState(false);
const [checkedAuth, setCheckedAuth] = useState(false);

const [alert, setAlert] = useState({
  show: false,
  type: "info",
  message: ""
});


/* ============================
CHECK LOGGED-IN USER
============================ */
useEffect(() => {

  debugLog("Checking logged-in user");

  const storedUser = localStorage.getItem("user");

  debugLog("Stored user raw:", storedUser);

  if (storedUser) {

    try {

      const parsed = JSON.parse(storedUser);

      debugLog("Parsed user:", parsed);

      if (parsed?.email) {

        debugLog("Found logged-in email:", parsed.email);

        setEmail(parsed.email);

        fetchBookings(parsed.email);

      }

    } catch (error) {

      debugError("Error parsing stored user:", error);

    }

  } else {

    debugLog("No stored user found");

  }

  setCheckedAuth(true);

}, []);


/* ============================
SHOW ALERT
============================ */
const showAlert = (type, message) => {

  debugLog("Showing alert:", type, message);

  setAlert({
    show: true,
    type,
    message
  });

  setTimeout(() => {

    debugLog("Hiding alert");

    setAlert(prev => ({ ...prev, show: false }));

  }, 4000);

};


/* ============================
FETCH BOOKINGS
============================ */
const fetchBookings = async (targetEmail) => {

  if (!targetEmail) {

    debugLog("fetchBookings called with empty email");

    return;
  }

  try {

    debugLog("Fetching bookings for:", targetEmail);

    setLoading(true);

    const response = await fetchBookingsByEmail(targetEmail);

    debugLog("Fetch bookings response:", response);

    if (response.status === 200) {

      debugLog("Bookings loaded:", response.data);

      setBookings(response.data);

    } else {

      debugError("Fetch failed:", response.message);

      setBookings([]);

    }

  } catch (error) {

    debugError("Fetch exception:", error);

    setBookings([]);

  } finally {

    setLoading(false);

    debugLog("Fetch bookings finished");

  }

};


/* ============================
FORMAT HELPERS
============================ */
const formatDateSafe = (value) => {

  try {

    if (!value) return "-";

    const date = new Date(value);

    if (isNaN(date)) {

      debugLog("Invalid date format:", value);

      return value;
    }

    return date.toLocaleDateString("en-IN", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric"
    });

  } catch (error) {

    debugError("Date format error:", error);

    return value;
  }

};

const formatTimeSafe = (value) => {

  try {

    if (!value) return "-";

    const date = new Date(value);

    if (isNaN(date)) {

      debugLog("Invalid time format:", value);

      return value;
    }

    return date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });

  } catch (error) {

    debugError("Time format error:", error);

    return value;
  }

};


/* ============================
GUEST EMAIL SUBMIT
============================ */
const handleEmailSubmit = () => {

  debugLog("Guest email submit:", inputEmail);

  if (!inputEmail.trim()) {

    showAlert("warning", "Please enter email address");

    return;
  }

  setEmail(inputEmail);

  fetchBookings(inputEmail);

};


/* ============================
JOIN SESSION
============================ */
const handleJoin = (booking) => {

  debugLog("Join clicked:", booking);

  if (booking.zoomLink) {

    debugLog("Opening zoom link:", booking.zoomLink);

    window.open(booking.zoomLink, "_blank");

  } else {

    debugLog("Zoom link not available");

    showAlert("warning", "Zoom link is not available yet.");

  }

};


return (
<>
  <Navbar />

  {alert.show && (
    <div className={`custom-alert ${alert.type}`}>
      {alert.message}
    </div>
  )}

  <div className="bookings-container">

    {/* HEADER */}
    <div className="bookings-header">

      <button
        className="back-btn"
        onClick={() => {
          debugLog("Navigating to events page");
          navigate("/events");
        }}
      >
        ← Back to events
      </button>

      <p className="subtitle">
        View and manage your booked sessions
      </p>

    </div>


    {/* EMAIL CARD */}
    {checkedAuth && !email && (
      <div className="email-card">

        <h3>Find your bookings</h3>

        <div className="email-input-group">

          <input
            type="email"
            placeholder="Enter your email address"
            value={inputEmail}
            onChange={(e) => {
              debugLog("Email input changed:", e.target.value);
              setInputEmail(e.target.value);
            }}
          />

          <button onClick={handleEmailSubmit}>
            View Bookings
          </button>

        </div>

      </div>
    )}


    {/* LOADING */}
    {loading && (
      <div className="status-card">
        Loading your bookings...
      </div>
    )}


    {/* BOOKINGS */}
    {!loading && email && bookings.length > 0 && (

      <div className="bookings-list">

     {bookings.map((booking) => {
  const isCancelled = booking.cancelled; // check cancellation status

  return (
    <div className="booking-card" key={booking.bookingId}>

      <div className="booking-header">

        <h3>{booking.eventTitle}</h3>

        <span className={`badge ${isCancelled ? "cancelled" : "confirmed"}`}>
          {isCancelled ? "Cancelled" : "Confirmed"}
        </span>

      </div>

      {booking.eventDescription && (
        <p className="description">
          {booking.eventDescription}
        </p>
      )}

      <div className="details-list">

        <div className="detail-row">
          <span className="detail-label">Date:</span>
          <span className="detail-value">
            {formatDateSafe(booking.eventStartTime || booking.eventDate)}
          </span>
        </div>

        <div className="detail-row">
          <span className="detail-label">Time:</span>
          <span className="detail-value">
            {formatTimeSafe(booking.eventStartTime || booking.eventTime)}
          </span>
        </div>

        {booking.eventDuration && (
          <div className="detail-row">
            <span className="detail-label">Duration:</span>
            <span className="detail-value">
              {booking.eventDuration} minutes
            </span>
          </div>
        )}

      </div>

      <button
        onClick={() => handleJoin(booking)}
        className="join-btn"
        disabled={isCancelled} // ✅ disable button if cancelled
      >
        {isCancelled ? "Session Cancelled" : "Join Session"}
      </button>

    </div>
  );
})}

      </div>

    )}


    {/* EMPTY */}
    {!loading && email && bookings.length === 0 && (

      <div className="status-card">

        <h3>No bookings found</h3>

        <p>You haven't booked any sessions yet.</p>

        <button
          className="primary-btn"
          onClick={() => {
            debugLog("Browse events clicked");
            navigate("/events");
          }}
        >
          Browse Events
        </button>

      </div>

    )}

  </div>

</>
);

};

export default MyBookingsPage;